import { AuthShell } from "./AuthShell";

export type LaunchPhase = "ready" | "opening" | "opened" | "error" | "subscribed";

interface CheckoutLaunchProps {
  planName: string;
  periodLabel: string;
  priceLabel: string;
  phase: LaunchPhase;
  onOpenCheckout: () => void;
  onSeeAllPlans: () => void;
  accountEmail: string | null;
  onSignOut: () => void;
}

// What the funnel lands on after sign-in when a plan was already chosen on the
// marketing site (a /get?plan=… deep-link). Instead of re-rendering the whole
// pricing grid, we show a single calm "taking you to checkout" screen — the
// plan is decided, the only job left is the redirect to the hosted Lemon
// Squeezy checkout page (same tab; wallets like Apple/Google Pay only render
// on a top-level page, never in the old embedded overlay).
const COPY: Record<LaunchPhase, { title: string; body: string }> = {
  // "ready" is the no-auto-redirect landing (e.g. the buyer pressed Back from
  // the LS page): the plan is still picked, checkout is one explicit click.
  ready: {
    title: "Ready when you are",
    body: "Your plan is picked — open the secure checkout whenever you're ready.",
  },
  opening: {
    title: "Taking you to secure checkout",
    body: "We're preparing your checkout page — this only takes a second.",
  },
  opened: {
    title: "Redirecting to checkout",
    body: "You're being taken to the secure checkout page. Not redirected? Reopen it below.",
  },
  error: {
    title: "Couldn't start checkout",
    body: "Something interrupted the checkout hand-off. Try again, or pick from all plans.",
  },
  subscribed: {
    title: "You're already subscribed",
    body: "This workspace already has an active plan. Manage it from Admin → Plan & billing.",
  },
};

export function CheckoutLaunch({
  planName,
  periodLabel,
  priceLabel,
  phase,
  onOpenCheckout,
  onSeeAllPlans,
  accountEmail,
  onSignOut,
}: CheckoutLaunchProps) {
  const copy = COPY[phase];
  const busy = phase === "opening";

  return (
    <AuthShell>
      <div className={`checkout-launch checkout-launch--${phase}`} role="status" aria-live="polite">
        <div className="checkout-launch__summary">
          <span className="checkout-launch__plan">{planName}</span>
          <span className="checkout-launch__meta">
            {periodLabel} · {priceLabel}/seat/mo
          </span>
        </div>

        <div className="checkout-launch__status">
          {busy ? (
            <span className="checkout-launch__spinner" aria-hidden="true" />
          ) : (
            <span
              className={`checkout-launch__glyph checkout-launch__glyph--${phase}`}
              aria-hidden="true"
            />
          )}
          <h1 className="checkout-launch__title">{copy.title}</h1>
          <p className="checkout-launch__body">{copy.body}</p>
        </div>

        <div className="checkout-launch__actions">
          {phase !== "subscribed" && (
            <button
              type="button"
              className="checkout-launch__primary"
              onClick={onOpenCheckout}
              disabled={busy}
            >
              {phase === "error"
                ? "Try again"
                : phase === "ready"
                  ? "Open checkout"
                  : "Reopen checkout"}
            </button>
          )}
          <button type="button" className="checkout-launch__ghost" onClick={onSeeAllPlans}>
            See all plans
          </button>
        </div>

        {accountEmail && (
          <p className="checkout-launch__account">
            Signed in as <span>{accountEmail}</span>
            <button type="button" onClick={onSignOut}>
              Switch account
            </button>
          </p>
        )}
      </div>
    </AuthShell>
  );
}
